import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { Family } from './useFamily';
import { CreateFamilyPayload } from './useCreateFamily';

type UpdateFamilyPayload = Partial<CreateFamilyPayload>;

export function useUpdateFamily() {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    error,
    isPending: isLoading,
    reset,
  } = useMutation({
    mutationFn: ({ id, values }: { id: string; values: UpdateFamilyPayload }) =>
      api.put<Family>(`families/${id}`, values),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['families'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      // Membership changes (e.g. adding a member) alter the family's aggregated
      // document and file lists, which usually live on an inactive tab. A default
      // invalidation only refetches active queries, so force a refetch of both so
      // a new member's documents appear without a manual page refresh.
      queryClient.invalidateQueries({
        queryKey: ['families', id, 'all-documents'],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['families', id, 'data-upload-files'],
        refetchType: 'all',
      });
    },
  });

  const updateFamily = async (id: string, values: UpdateFamilyPayload) => {
    return mutateAsync({ id, values });
  };

  return {
    updateFamily,
    error,
    isLoading,
    reset,
  };
}
